import type { DiagramConfiguration } from '../@types/diagram';
import { useDiagramContext } from '../context/diagram.context';
import useAIGenerated from './use-ai-generated';
import useRecord from './use-record';
import useSavedDiagram from './use-saved-diagram';

/**
 * Custom hook for managing diagram data state based on the data source (AI-generated or record or previously saved diagram).
 *
 * @returns An object containing:
 * - `loading` - Boolean indicating if diagram data is being fetched
 * - `error` - Boolean indicating if there was an error fetching the diagram data
 * - `diagramData` - The diagram data, either AI-generated or from record
 *
 * @example
 * ```typescript
 * const { loading, error, diagramData } = useDiagramData();
 * ```
 */
const useDiagramData = () => {
  const { isAIGenerated, isSavedDiagram } = useDiagramContext();

  // handles a record source
  const { isFetching: isRecordFetching, isError: isRecordError, diagramData: recordDiagramData } = useRecord();
  // handles an AI-generated source
  const { isFetching: isAIFetching, diagramData: aiGeneratedDiagramData } = useAIGenerated();
  // handles a diagram only saved source
  const { isFetching: isSavedFetching, isError: isSavedError, diagramData: savedDiagramData } = useSavedDiagram();

  const loading =
    (!isAIGenerated && !isSavedDiagram && isRecordFetching && !recordDiagramData) ||
    (isAIGenerated && isAIFetching && !aiGeneratedDiagramData) ||
    (isSavedDiagram && isSavedFetching && !savedDiagramData);

  const error =
    (!isAIGenerated && !isSavedDiagram && !isRecordFetching && isRecordError) ||
    (isSavedDiagram && !isSavedFetching && isSavedError);

  let diagramData: DiagramConfiguration | undefined;
  if (isSavedDiagram) {
    diagramData = savedDiagramData;
  } else if (isAIGenerated) {
    diagramData = aiGeneratedDiagramData;
  } else {
    diagramData = recordDiagramData;
  }

  return {
    loading,
    error,
    diagramData,
  };
};

export default useDiagramData;
